import { SelectionModel } from '@angular/cdk/collections';
import { Component, OnInit } from '@angular/core';
import { MediaChange, MediaObserver } from '@angular/flex-layout';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map, shareReplay, startWith, Subscription, switchMap, tap, withLatestFrom } from 'rxjs';
import { Logger } from 'src/app/@shared';
import { Event, EventDomain, EventService } from '../..';
import { MatDialog } from '@angular/material/dialog';
import { CloneComponent } from '../../pages/clone/clone.component';

const log = new Logger('EventListTableComponent');

@Component({
  selector: 'app-event-list-table',
  templateUrl: './event-list-table.component.html',
  styleUrls: ['./event-list-table.component.scss']
})

export class EventListTableComponent<TEvent extends Event> implements OnInit {

  viewModel$ = this.eventService.events$.pipe(
    withLatestFrom(
      this.eventService.page$,
      this.eventService.totalRecords$
    ),
    tap(([events]) => {
      this.eventsList = events;
      this.selection.clear();
    }),
    switchMap(([events, page, totalRecords]) => {
      if (events.length === 0 && page.pageIndex > 0) {
        const resetPage = { ...page, previousPageIndex: 0, pageIndex: 0 };
        this.eventService.page(resetPage);
        this.eventService.reload();
      }

      // Extract event IDs
      const eventIds = events.map(event => event.Detail.Id);

      // Fetch event statuses asynchronously
      return this.eventService.getEventStatuses(eventIds).pipe(
        map(statusMap => {
          const updatedEvents = events.map(event => ({
            ...event,
            EventStatus: statusMap[event.Detail.Id] || { Approved: '0', Pending: '0', Suppressed: '0' }
          }));

          this.eventsList = updatedEvents; // Update the eventsList with the new data
          return { events: updatedEvents, page, totalRecords };
        }),
        startWith({ events, page, totalRecords }) // Ensure UI updates immediately with events
      );
    }),
    shareReplay(1) // Ensure multiple subscribers get the same data
  );

  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'HasWorkflow', 'EventName', 'StartDate', 'DivisionNames', 'EndDate', 'EventTypeName', 'EventStatus', 'ClientKey', 'Actions'];
  eventsList: EventDomain<TEvent>[] = [];
  selection = new SelectionModel<EventDomain<TEvent>>(true, []);
  eventId: string = '0';


  constructor(private eventService: EventService<TEvent>, private mediaObserver: MediaObserver, private router: Router, private route: ActivatedRoute, private dialog: MatDialog) { }

  ngOnInit(): void {
    log.debug('init');
    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'HasWorkflow', 'EventName', 'Actions'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'HasWorkflow', 'EventName', 'StartDate', 'Actions'];
        }
        else {
          this.displayedColumns = ['select', 'HasWorkflow', 'EventName', 'StartDate', 'DivisionNames', 'EndDate', 'EventTypeName', 'EventStatus', 'ClientKey', 'Actions'];
        }
      });
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.eventService.page(pageEvent);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.eventsList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.eventsList.forEach(row => this.selection.select(row));
  }

  public getSelectedSectionRecords() {
    return this.selection.selected;
  }

  public clearSelection() {
    this.selection.clear();
  }

  openDialog(record: any): void {

    const dialogRef = this.dialog.open(CloneComponent, {
      width: '80%',
      height: '60%',
      data: { eventId: record.Detail.Id }
    });
  }

  onSort(sortState: Sort): void {
    this.eventService.sort(sortState);
    this.selection.clear();
  }

  onRowDoubleClick(id: string) {
    this.router.navigate([`${id}/offers`], { relativeTo: this.route });
  }

  ngOnDestroy(): void {
    this.flexMediaWatcher.unsubscribe();
  }

}
